/**
 * The browser side of reading a site plan sketch (Cleaning Visual, z8nrz7f731):
 * the picture drawn onto a canvas, its outlines read by `readSkeleton`, and the
 * label inside each building read by OCR. Nothing is uploaded: the picture
 * never leaves the browser, only what was read off it.
 */
import { parseLabel, readSkeleton, toPercent, type PixelBox } from './sitePlanSkeleton'
import type { LinkKind } from '@/api/facilityApi'

export interface SitePlanReading {
  buildings: { code?: string; name: string; x: number; y: number; width: number; height: number }[]
  connections: { from: string; to: string; kind: LinkKind }[]
}

/** Any picture (PNG, JPG, WebP, SVG) on a white canvas at most `maxWidth` wide. */
async function draw(file: File, maxWidth = 1400): Promise<HTMLCanvasElement> {
  const url = URL.createObjectURL(file)
  try {
    const img = new Image()
    img.src = url
    await img.decode()
    // An SVG with no size of its own reports 0: draw it at a sensible size.
    const w0 = img.naturalWidth || 1400
    const h0 = img.naturalHeight || 900
    const scale = Math.min(1, maxWidth / w0)
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(w0 * scale))
    canvas.height = Math.max(1, Math.round(h0 * scale))
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) throw new Error('This browser cannot read pictures.')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    return canvas
  } catch {
    throw new Error('That picture could not be opened. Choose a PNG, JPG, WebP or SVG.')
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** Fails after `ms`: the OCR worker does not always reject when its set-up fails. */
const within = <T>(p: Promise<T>, ms: number) =>
  Promise.race([p, new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timed out')), ms))])

/** The text inside each box; '' where nothing could be read (or OCR is unavailable). */
async function readLabels(canvas: HTMLCanvasElement, boxes: PixelBox[]): Promise<string[]> {
  try {
    // Loaded on first use only. The English data is served by the app (see vite.config.ts, cgtTessdata).
    const mod = await import('tesseract.js')
    const { createWorker, PSM } = (mod as unknown as { default?: typeof mod }).default ?? mod
    const worker = await within(createWorker('eng', 1, { langPath: `${window.location.origin}/tessdata`, errorHandler: () => undefined }), 30_000)
    try {
      await worker.setParameters({
        tessedit_pageseg_mode: PSM.SPARSE_TEXT,
        tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789&-/. ',
      })
      const out: string[] = []
      for (const b of boxes) {
        // Inside the outline only, enlarged so small labels read well.
        const inset = 6
        const w = Math.max(1, b.x1 - b.x0 + 1 - 2 * inset)
        const h = Math.max(1, b.y1 - b.y0 + 1 - 2 * inset)
        const scale = Math.max(1, Math.min(3, 240 / Math.min(w, h)))
        const crop = document.createElement('canvas')
        crop.width = Math.round(w * scale) + 40
        crop.height = Math.round(h * scale) + 40
        const ctx = crop.getContext('2d')
        if (!ctx) {
          out.push('')
          continue
        }
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, crop.width, crop.height)
        ctx.drawImage(canvas, b.x0 + inset, b.y0 + inset, w, h, 20, 20, w * scale, h * scale)
        const { data: read } = await within(worker.recognize(crop), 15_000)
        out.push(read.text)
      }
      return out
    } finally {
      await worker.terminate()
    }
  } catch {
    // Labels are a convenience: the buildings are still read, and named in the review.
    return boxes.map(() => '')
  }
}

export async function readSitePlanPicture(file: File): Promise<SitePlanReading> {
  const canvas = await draw(file)
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) throw new Error('This browser cannot read pictures.')
  const { width: W, height: H } = canvas
  const skeleton = readSkeleton({ width: W, height: H, data: ctx.getImageData(0, 0, W, H).data })
  const labels = skeleton.buildings.length ? await readLabels(canvas, skeleton.buildings) : []
  const buildings = skeleton.buildings.map((b, i) => ({ ...parseLabel(labels[i] ?? '', i), ...toPercent(b, W, H) }))
  // Two buildings read with the same label stay apart: the second is numbered.
  const refs = buildings.map((b) => b.code ?? b.name)
  buildings.forEach((b, i) => {
    if (refs.indexOf(refs[i]) !== i) {
      b.code = undefined
      b.name = `${b.name} ${i + 1}`
      refs[i] = b.name
    }
  })
  return {
    buildings,
    connections: skeleton.connections.map((c) => ({ from: refs[c.a], to: refs[c.b], kind: c.kind })),
  }
}
